import { useCallback, useEffect, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useActionData, useFetcher, useParams, useSubmit } from '@remix-run/react';
import { withZod } from '@remix-validated-form/with-zod';
import { Upload } from '~/components/images/Upload';
import PasswordValidation from '~/components/password-validation';
import { TermsAndConditionsDialog } from '~/components/terms-condition-dialog';
import { Avatar, AvatarFallback } from '~/components/ui/avatar';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '~/components/ui/dialog';
import { Form, FormField, FormItem, FormLabel, FormMessage } from '~/components/ui/form';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { PasswordInput } from '~/components/ui/password-input';
import { PhoneInput } from '~/components/ui/phone-input';
import Viewer from '~/components/ui/rich-text/viewer';
import { Select, SelectContent, SelectItem, SelectTrigger } from '~/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/components/ui/tooltip';
import { useToast } from '~/components/ui/use-toast';
import { formatDateTime, formatFileSize, formatPrice, getCategoryName } from '~/lib/utils';
import { getAuctionDetailsBasic } from '~/services/api/auctions';
import { createUserProfileValidator } from '~/services/models/addresses';
import { LoginInput, loginValidator } from '~/services/models/auth';
import { UserCreateInput, userCreateValidator } from '~/services/models/users';
import { AuctionPropertyType, AuctionRegistrationStatus, FetcherData } from '~/types';
import { CloudUpload, Dot, LoaderCircle, Trash, X } from 'lucide-react';
import Dropzone from 'react-dropzone';
import { useForm } from 'react-hook-form';
import { getCountryCallingCode } from 'react-phone-number-input';
import { ValidatedForm } from 'remix-validated-form';

interface CreateUserResponse {
  status: string;
  newUserId: string;
  firstName: string;
  lastName: string;
}

interface UploadDocument {
  file: File;
  src?: string;
  documentType: string;
}

export const loginValidation = withZod(loginValidator);
export const createUsersValidator = withZod(userCreateValidator);
export const userCreateProfileValidator = withZod(createUserProfileValidator);

interface AuctionRegistrationProps {
  isLoggedIn: boolean;
  userId: string | undefined;
  data: Awaited<ReturnType<typeof getAuctionDetailsBasic>>['data'];
}

export default function AuctionRegistration({ data, isLoggedIn, userId }: AuctionRegistrationProps) {
  const { slug } = useParams();
  const actionData = useActionData<any>();
  const userCreationFetcher = useFetcher<CreateUserResponse>();
  const fetcher = useFetcher();
  const submit = useSubmit();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [termsChecked, setTermsChecked] = useState(false);
  const [username, setUsername] = useState({
    firstName: '',
    lastName: '',
  });
  const [newUserId, setNewUserId] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string>();
  const [selectedState, setSelectedState] = useState<string>();
  const [approvalRequired, setApprovalRequired] = useState(false);
  const [documentRequired, setDocumentRequired] = useState(false);
  const [depositRequired, setDepositRequired] = useState(false);
  const [userDocuments, setUserDocuments] = useState<Array<UploadDocument>>([]);
  const [documentUploadName, setDocumentUploadName] = useState<string | null>(null);
  const [documentUrl, setDocumentUrl] = useState('');
  const [paymentDocument, setPaymentDocument] = useState<UploadDocument | null>(null);
  const [isProperty, setIsProperty] = useState(false);
  const [totalDeposit, setTotalDeposit] = useState(0);
  const [checkedState, setCheckedState] = useState<{ [key: string]: boolean }>({});
  const [password, setPassword] = useState('');
  const [openTermsAndConditionDialog, setOpenTermsAndConditionDialog] = useState(false);

  const [validations, setValidations] = useState({
    length: false,
    casing: false,
    specialOrNumber: false,
  });

  const identificationDocument = userDocuments.find((document) => document.documentType === 'identificationDocument');

  const loginForm = useForm<LoginInput>({
    resolver: zodResolver(loginValidator),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const registerCreateUserForm = useForm<UserCreateInput>({
    resolver: zodResolver(userCreateValidator),
    defaultValues: {
      firstName: '',
      lastName: '',
      phone: '+60',
      email: '',
      password: '',
    },
  });

  const handleTermsChange = () => {
    setTermsChecked(!termsChecked);
  };

  const isButtonDisabled = !termsChecked;
  const auctionCategory = getCategoryName(data?.products[0]?.productsToCategories[0]?.categoryId || '');
  const depositPercentage = data?.depositPercentage || 0;

  useEffect(() => {
    if (!open) {
      setStep(isLoggedIn ? 4 : 1);
    }
    if (data?.propertyType === AuctionPropertyType.BANK_PROPERTY) {
      setIsProperty(true);
    }
  }, [open, isLoggedIn, data]);

  useEffect(() => {
    setValidations({
      length: password.length >= 7,
      casing: /[a-z]/.test(password) && /[A-Z]/.test(password),
      specialOrNumber: /[!@#$%^&*(),.?":{}|<>_+=\-/0-9]/.test(password),
    });
  }, [password]);

  useEffect(() => {
    if (userCreationFetcher.data && userCreationFetcher.data.status === 'success') {
      setNewUserId(userCreationFetcher.data.newUserId);
      setUsername({
        firstName: userCreationFetcher.data.firstName,
        lastName: userCreationFetcher.data.lastName,
      });
      setStep(2);
      setTermsChecked(false);
    }
  }, [userCreationFetcher.data]);

  useEffect(() => {
    const fetcherData: any = fetcher.data;
    if (fetcherData?.status === 'success' && fetcherData?.action === 'login') {
      setStep(4);
    }

    if (fetcherData?.error) {
      toast.toast({
        variant: 'destructive',
        title: 'Error',
        description:
          typeof fetcherData?.error === 'string' ? fetcherData?.error : 'Something went wrong. Please try again',
      });
    }
  }, [fetcher]);

  useEffect(() => {
    if (data?.approvalRequired === true) {
      setApprovalRequired(true);
    }
    if (data?.documentRequired === true) {
      setDocumentRequired(true);
    }
    if (data?.depositRequired === true) {
      setDepositRequired(true);
    }
  }, [data]);

  const uploadFile = useCallback(
    (documentName: string, file: File) => {
      const formData = new FormData();

      if (documentName === 'paymentDocument') {
        setPaymentDocument({
          documentType: documentName,
          file,
        });
        formData.append('paymentDocument', file);
      } else {
        setDocumentUploadName(documentName);
        setUserDocuments((prev) => {
          if (prev.length === 0) return [{ file, documentType: documentName }];
          const fileUploaded = prev.find((p) => p.documentType === documentName);

          if (!fileUploaded) return [...prev, { file, documentType: documentName }];

          return prev.map((prevDoc) =>
            prevDoc.documentType === documentName ? { ...prevDoc, file } : { file, documentType: documentName },
          );
        });

        formData.append('document', file);
      }

      submit(formData, {
        method: 'post',
        encType: 'multipart/form-data',
      });
    },
    [submit, setUserDocuments],
  );

  useEffect(() => {
    if (actionData?.paymentDocument) {
      setPaymentDocument(
        (prev) =>
          prev && {
            ...prev,
            src: actionData.paymentDocument,
          },
      );
    }

    if (actionData?.document && documentUploadName) {
      setUserDocuments((prev) => {
        return prev.map((prevDoc) =>
          prevDoc.documentType === documentUploadName ? { ...prevDoc, src: actionData?.document } : { ...prevDoc },
        );
      });
      setDocumentUrl(actionData.document);
      setDocumentUploadName(null);
    }

    if (actionData?.error) {
      toast.toast({
        variant: 'destructive',
        title: 'Error',
        description: actionData?.error?.message || 'Something went wrong. Please try again',
      });
      actionData.error = undefined;
    }
  }, [actionData, documentUrl]);

  useEffect(() => {
    const fetcherData = fetcher.data as FetcherData;
    if (fetcherData?.status === 'success' && fetcherData?.action === 'login') {
      toast.toast({
        title: 'Success',
        description: 'You have successfully logged in.',
      });
    }

    if (fetcherData?.error) {
      toast.toast({
        variant: 'destructive',
        title: 'Error',
        description:
          typeof fetcherData?.error === 'string' ? fetcherData?.error : 'Something went wrong. Please try again',
      });
    }
  }, [fetcher.data]);

  useEffect(() => {
    const fetcherData = userCreationFetcher.data as any;
    if (fetcherData?.status === 'success') {
      toast.toast({
        title: 'Success',
        description: 'You have successfully created an account.',
      });
    }

    if (fetcherData?.error) {
      toast.toast({
        variant: 'destructive',
        title: 'Error',
        description:
          typeof fetcherData?.error?.message === 'string'
            ? fetcherData?.error?.message
            : 'Something went wrong. Please try again',
      });
    }
  }, [userCreationFetcher.data]);

  useEffect(() => {
    if (data?.products) {
      const initialCheckedState = data.products.reduce(
        (state, product) => {
          state[product.id] = false;
          return state;
        },
        {} as { [key: string]: boolean },
      );
      setCheckedState(initialCheckedState);
    }
  }, []);

  useEffect(() => {
    if (data?.products) {
      const total = data.products.reduce((sum, product) => {
        const reservedPrice = product?.property?.reservedPrice ?? 0;
        const depositAmount = (reservedPrice * depositPercentage) / 100;
        return sum + (checkedState[product.id] ? depositAmount : 0);
      }, 0);
      setTotalDeposit(total);
    }
  }, [depositPercentage, checkedState]);

  const handleCheckedChange = (id: string) => {
    setCheckedState((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };

  const getNextStep = (currentStep: number): number => {
    switch (currentStep) {
      case 1: // Create Account
        return isLoggedIn ? 4 : 2;
      case 2: // Create Payment Method
        return depositRequired ? 3 : 4;
      case 3: // Create Profile
        return 4;
      case 4: // Terms and Conditions
        if (isProperty) {
          return 7;
        }
        return documentRequired || depositRequired ? 5 : 6;
      case 5: // Upload Documents & payment method
        return 6;
      case 7: // Property Deposit
        return 5;
      case 8: // Existing user login
        return 4;
      default:
        return 6; // Awaiting Approval
    }
  };

  const createAccount = (
    <>
      <ValidatedForm
        validator={createUsersValidator}
        method="post"
        action="/api/register-user"
        fetcher={userCreationFetcher}
      >
        <Form {...registerCreateUserForm}>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col items-center justify-center">
              <p className="text-xl font-medium text-black">Create Account</p>
              <p className="text-sm font-normal text-slate-500">Sign up to register for this auction</p>
            </div>
            <div className="my-4 flex flex-col gap-2">
              <div className="flex flex-row gap-4">
                <div className="grid w-full max-w-sm items-center gap-1.5">
                  <FormField
                    control={registerCreateUserForm.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem className="mb-2">
                        <FormLabel>First Name</FormLabel>
                        <Input placeholder="Enter your first name" {...field} />
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid w-full max-w-sm items-center gap-1.5">
                  <FormField
                    control={registerCreateUserForm.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem className="mb-2">
                        <FormLabel>Last Name</FormLabel>
                        <Input placeholder="Enter your last name" {...field} />
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              <div className="w-full items-center gap-1.5">
                <FormField
                  control={registerCreateUserForm.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem className="mb-2">
                      <FormLabel>Phone number</FormLabel>
                      <PhoneInput
                        placeholder="Enter your phone number"
                        onCountryChange={(code) => {
                          if (code) {
                            field.onChange('+' + getCountryCallingCode(code) + field.value);
                            return code;
                          }
                        }}
                        required
                        {...field}
                      />
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>
              <div className="w-full items-center gap-1.5">
                <FormField
                  control={registerCreateUserForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="mb-2">
                      <FormLabel>Email</FormLabel>
                      <Input placeholder="Enter your email" {...field} />
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>
              <div className="w-full items-center gap-1.5">
                <FormField
                  control={registerCreateUserForm.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem className="mb-2">
                      <FormLabel>Password</FormLabel>
                      <PasswordInput placeholder="Enter your password" {...field} />
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>
              <PasswordValidation control={registerCreateUserForm.control} />
            </div>
            <div className="flex flex-col gap-3">
              <div className="flex items-center space-x-2">
                <Checkbox id="terms" onCheckedChange={handleTermsChange} checked={termsChecked} />
                <label
                  htmlFor="terms"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  I agree to the{' '}
                  <a className="cursor-pointer text-blue-600" onClick={() => setOpenTermsAndConditionDialog(true)}>
                    terms & conditions
                  </a>
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="reminder" />
                <label
                  htmlFor="reminder"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Please send me auction reminders via email
                </label>
              </div>
            </div>
            <Button
              variant={'primary'}
              onClick={() => {
                registerCreateUserForm.trigger();
              }}
              disabled={fetcher.state === 'submitting' || isButtonDisabled}
            >
              {fetcher.state === 'submitting' && <LoaderCircle className="mr-2 animate-spin" />}
              Join
            </Button>
            <div className="flex flex-row items-center justify-center gap-2 text-sm font-semibold">
              <p className="text-slate-700">Already have an account?</p>
              <Button
                variant={'ghost'}
                className="px-0 text-blue-500 hover:bg-transparent hover:text-blue-400 hover:underline"
                onClick={() => setStep(8)}
              >
                Sign In
              </Button>
            </div>
            <p className="text-xs font-normal text-slate-500">
              By continuing you agree to the <span className="text-blue-600">terms of service</span> and{' '}
              <span className="text-blue-600">privacy policy</span>. We may send you communications; you can set your
              preferences in your account.
            </p>
          </div>
        </Form>
      </ValidatedForm>
      <TermsAndConditionsDialog
        open={() => openTermsAndConditionDialog}
        onOpenChange={setOpenTermsAndConditionDialog}
      />
    </>
  );

  const createPaymentMethod = (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-center justify-center">
        <p className="text-xl font-medium text-black">Add Payment Method</p>
        <p className="text-pretty text-center text-sm font-normal text-slate-500">
          You won&apos;t be charged unless you win. This information will help you signal to auction houses that
          you&apos;re able to pay for the items you win.
        </p>
      </div>
      <div className="my-4 flex flex-col gap-2">
        <div className="w-full items-center gap-1.5">
          <Label htmlFor="cardName">Name on card</Label>
          <Input type="text" id="cardName" placeholder="Enter name on card" />
        </div>
        <div className="w-full items-center gap-1.5">
          <Label htmlFor="cardNo">Card number</Label>
          <Input type="text" id="cardNo" placeholder="Enter card number" />
        </div>
        <div className="flex flex-row gap-4">
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <Label htmlFor="expiration">Expiration</Label>
            <Input type="text" id="expiration" placeholder="Password" />
          </div>
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <Label htmlFor="ccv">CVV</Label>
            <Input type="text" id="ccv" placeholder="Enter CVV" />
          </div>
        </div>
        <div className="w-full items-center gap-1.5">
          <Label htmlFor="billingAddress">Billing Address</Label>
          <Input type="text" id="billingAddress" placeholder="Enter billing address" />
        </div>
        <div className="w-full items-center gap-1.5">
          <Label htmlFor="apt">Apt, Suite, etc (optional)</Label>
          <Input type="text" id="apt" placeholder="Enter apt, suite, etc" />
        </div>
        <div className="flex flex-row gap-4">
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <div className="col-span-1">
              <Label className="text-xs font-medium text-slate-900">Country</Label>
              <Select
                name="country"
                onValueChange={(value) => {
                  setSelectedCountry(value);
                }}
              >
                <SelectTrigger>{selectedCountry ? selectedCountry : 'Country'}</SelectTrigger>
                <SelectContent>
                  <SelectItem value={'Malaysia'}>Malaysia</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <div className="col-span-1">
              <Label className="text-xs font-medium text-slate-900">State/Province</Label>
              <Select
                name="state"
                onValueChange={(value) => {
                  setSelectedState(value);
                }}
              >
                <SelectTrigger>{selectedState ? selectedState : 'State/Province'}</SelectTrigger>
                <SelectContent>
                  <SelectItem value={'Johor'}>Johor</SelectItem>
                  <SelectItem value={'Kedah'}>Kedah</SelectItem>
                  <SelectItem value={'Kelantan'}>Kelantan</SelectItem>
                  <SelectItem value={'Kuala Lumpur'}>Kuala Lumpur</SelectItem>
                  <SelectItem value={'Labuan'}>Labuan</SelectItem>
                  <SelectItem value={'Melaka'}>Melaka</SelectItem>
                  <SelectItem value={'Negeri Sembilan'}>Negeri Sembilan</SelectItem>
                  <SelectItem value={'Pahang'}>Pahang</SelectItem>
                  <SelectItem value={'Penang'}>Penang</SelectItem>
                  <SelectItem value={'Perak'}>Perak</SelectItem>
                  <SelectItem value={'Perlis'}>Perlis</SelectItem>
                  <SelectItem value={'Putrajaya'}>Putrajaya</SelectItem>
                  <SelectItem value={'Sabah'}>Sabah</SelectItem>
                  <SelectItem value={'Sarawak'}>Sarawak</SelectItem>
                  <SelectItem value={'Selangor'}>Selangor</SelectItem>
                  <SelectItem value={'Terengganu'}>Terengganu</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <div className="flex flex-row gap-4">
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <Label htmlFor="city">City</Label>
            <Input type="text" id="city" placeholder="Enter city" />
          </div>
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <Label htmlFor="code">Postal Code</Label>
            <Input type="text" id="code" placeholder="Enter postal code" />
          </div>
        </div>
      </div>
      <Button variant={'primary'} onClick={() => setStep(getNextStep(2))}>
        Save and Continue
      </Button>
      <Button variant={'ghost'} onClick={() => setStep(getNextStep(2))}>
        Skip for now
      </Button>
    </div>
  );

  const createProfile = (
    <ValidatedForm
      validator={userCreateProfileValidator}
      method="post"
      id="create-user-profile-form"
      onSubmit={() => {
        setStep(getNextStep(3));
      }}
    >
      <input type="hidden" name="action" value="createUserProfile" />
      <input type="hidden" name="userId" value={newUserId || ''} />
      <div className="flex flex-col gap-4">
        <div className="flex flex-col items-center justify-center">
          <p className="text-xl font-medium text-black">You’re almost there!</p>
          <p className="text-pretty text-center text-sm font-normal text-slate-500">
            Complete your profile to start bidding
          </p>
        </div>
        <div className="my-4 flex flex-col gap-2">
          <div className="flex flex-row gap-4">
            <div className="grid w-full max-w-sm items-center gap-1.5">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                type="text"
                id="firstName"
                placeholder="First Name"
                defaultValue={username.firstName}
                name="firstName"
                required
              />
            </div>
            <div className="grid w-full max-w-sm items-center gap-1.5">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                type="text"
                id="lastName"
                placeholder="Last Name"
                defaultValue={username.lastName}
                name="lastName"
                required
              />
            </div>
          </div>
          <div className="w-full items-center gap-1.5">
            <Label htmlFor="shippingAddress">Contact Number</Label>
            <Input id="contactNumber" placeholder="Enter contact number" name="contactNumber" required />
          </div>
          <div className="flex flex-row gap-4">
            <div className="grid w-full max-w-sm items-center gap-1.5">
              <div className="col-span-1">
                <Label className="text-xs font-medium text-slate-900">Country</Label>
                <Select
                  name="country"
                  onValueChange={(value) => {
                    setSelectedCountry(value);
                  }}
                  required
                >
                  <SelectTrigger>{selectedCountry ? selectedCountry : 'Country'}</SelectTrigger>
                  <SelectContent>
                    <SelectItem value={'Malaysia'}>Malaysia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid w-full max-w-sm items-center gap-1.5">
              <div className="col-span-1">
                <Label className="text-xs font-medium text-slate-900">State/Province</Label>
                <Select
                  name="state"
                  onValueChange={(value) => {
                    setSelectedState(value);
                  }}
                  required
                >
                  <SelectTrigger>{selectedState ? selectedState : 'State/Province'}</SelectTrigger>
                  <SelectContent>
                    <SelectItem value={'Johor'}>Johor</SelectItem>
                    <SelectItem value={'Kedah'}>Kedah</SelectItem>
                    <SelectItem value={'Kelantan'}>Kelantan</SelectItem>
                    <SelectItem value={'Kuala Lumpur'}>Kuala Lumpur</SelectItem>
                    <SelectItem value={'Labuan'}>Labuan</SelectItem>
                    <SelectItem value={'Melaka'}>Melaka</SelectItem>
                    <SelectItem value={'Negeri Sembilan'}>Negeri Sembilan</SelectItem>
                    <SelectItem value={'Pahang'}>Pahang</SelectItem>
                    <SelectItem value={'Penang'}>Penang</SelectItem>
                    <SelectItem value={'Perak'}>Perak</SelectItem>
                    <SelectItem value={'Perlis'}>Perlis</SelectItem>
                    <SelectItem value={'Putrajaya'}>Putrajaya</SelectItem>
                    <SelectItem value={'Sabah'}>Sabah</SelectItem>
                    <SelectItem value={'Sarawak'}>Sarawak</SelectItem>
                    <SelectItem value={'Selangor'}>Selangor</SelectItem>
                    <SelectItem value={'Terengganu'}>Terengganu</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="w-full items-center gap-1.5">
            <Label htmlFor="shippingAddress">Shipping Address</Label>
            <Input type="text" id="shippingAddress" placeholder="Enter shipping address" name="line1" required />
          </div>
          <div className="w-full items-center gap-1.5">
            <Label htmlFor="apt">Apt, Suite, etc (optional)</Label>
            <Input type="text" id="apt" placeholder="Enter apt, suite, etc" name="line2" required />
          </div>
          <div className="flex flex-row gap-4">
            <div className="grid w-full max-w-sm items-center gap-1.5">
              <Label htmlFor="city">City</Label>
              <Input type="text" id="city" placeholder="Enter city" name="city" required />
            </div>
            <div className="grid w-full max-w-sm items-center gap-1.5">
              <Label htmlFor="code">Postal Code</Label>
              <Input type="text" id="code" placeholder="Enter postal code" name="postcode" required />
            </div>
          </div>
        </div>
        <Button variant={'primary'} type="submit">
          Save and Continue
        </Button>

        <Button
          variant={'ghost'}
          onClick={() => {
            setStep(getNextStep(3));
          }}
        >
          Skip for now
        </Button>
      </div>
    </ValidatedForm>
  );

  const termAndConditions = (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col border border-slate-200 p-4">
        <div className="flex flex-row items-center text-xs font-normal text-slate-500">
          <p>{auctionCategory}</p>
          <Dot />
          <p>Start: {formatDateTime(data?.auctionStart)} GMT +8</p>
        </div>
        <p>{data?.name || ''}</p>
        <div className="my-4 flex flex-row items-center gap-3">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-black text-white">{data?.auctionHouse?.name.charAt(0) || ''}</AvatarFallback>
          </Avatar>
          <p className="text-sm font-normal text-slate-900">{data?.auctionHouse?.name || ''}</p>
        </div>
        <Viewer content={data?.description || ''} />
      </div>
      <div className="my-3 flex flex-col gap-3">
        <div className="flex items-center space-x-2">
          <Checkbox id="terms" onCheckedChange={handleTermsChange} checked={termsChecked} />
          <label
            htmlFor="terms"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            I agree to the <span className="text-blue-600">terms & conditions</span>
          </label>
        </div>
        <div className="flex items-center space-x-2">
          <Checkbox id="reminder" />
          <label
            htmlFor="reminder"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Please send me auction reminders via email
          </label>
        </div>
      </div>
      <Button
        variant={'primary'}
        onClick={() => {
          setStep(getNextStep(4));
        }}
        disabled={isButtonDisabled}
      >
        Register for Auction
      </Button>
    </div>
  );

  const uploadDocuments = (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-center justify-center">
        <p className="text-xl font-medium text-black">Complete Registration</p>
        <p className="text-pretty text-center text-sm font-normal text-slate-500">
          Please complete the following steps to complete your registration
        </p>
      </div>

      {documentRequired && (
        <div className="flex flex-col gap-2">
          <div>
            <p className="text-sm font-semibold text-slate-900">Upload Documents</p>
            <p className="text-xs font-normal text-slate-500">
              This auction requires you to submit documents for verification purposes. Accepted documents include: PDF,
              JPG, PNG
            </p>
          </div>

          {((data?.registrationDocuments || []) as Array<Record<string, any>>).map((document, index) => {
            const userDoc = userDocuments.find((doc) => doc.documentType === document.name);
            return (
              <div key={index} className="flex flex-col gap-2">
                <p className="text-sm font-semibold">{document.name}</p>
                {userDoc ? (
                  <div className="flex h-16 flex-row items-center gap-4 rounded-lg border border-slate-200 px-5 py-3">
                    <CloudUpload className="h-6 w-6" />
                    <div>
                      <p className="font-medium">{userDoc.file.name}</p>
                      <p className="text-left text-muted-foreground">{formatFileSize(userDoc.file.size)}</p>
                    </div>
                    <div className="flex-grow" />
                    <button
                      onClick={() => {
                        setUserDocuments((prev) => prev.filter((p) => p.documentType !== document.name));
                      }}
                    >
                      <Trash className="h-8 w-8 rounded-lg border border-slate-200 p-1.5" />
                    </button>
                  </div>
                ) : (
                  <Dropzone
                    maxSize={10000000}
                    onDropAccepted={(acceptedFiles) => {
                      uploadFile(document.name, acceptedFiles[0]);
                    }}
                    onDropRejected={() => {
                      toast.toast({
                        title: 'Error',
                        description: 'File size exceeds the maximum limit of 10MB',
                        variant: 'destructive',
                      });
                    }}
                    accept={{
                      'application/pdf': ['.pdf'],
                      'image/jpeg': ['.jpg', '.jpeg'],
                      'image/png': ['.png'],
                    }}
                    maxFiles={1}
                  >
                    {({ getRootProps, getInputProps }) => (
                      <div key={index} {...getRootProps()}>
                        <input {...getInputProps()} />
                        <div className="flex h-16 flex-row items-center gap-4 rounded-lg border border-dashed border-slate-300 px-5 py-3">
                          <Upload className="h-6 w-6" />
                          <div>
                            <p className="text-sm font-medium">Upload document</p>
                            <p className="text-xs text-muted-foreground">
                              Drag and drop files here or {''}
                              <span className="text-blue-500">Choose File</span> (Max file size 10MB)
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </Dropzone>
                )}
              </div>
            );
          })}
        </div>
      )}

      {data?.propertyType === AuctionPropertyType.BANK_PROPERTY && (
        <div>
          <hr className="my-4" />
          <div className="flex flex-col">
            <p className="text-sm font-semibold text-slate-900">Identification Document (MyKad)</p>
            {/* <p className="text-xs font-normal text-slate-500">
              This auction requires a copy of your identification document. Please
              note that if you win, the deposit is deducted from the final
              amount; if you lose, the deposit is refunded.
            </p> */}
          </div>
          <div className="mt-4 flex flex-col gap-2">
            {identificationDocument ? (
              <div className="flex h-16 flex-row items-center gap-4 rounded-lg border border-slate-200 px-5 py-3">
                <CloudUpload className="h-6 w-6" />
                <div>
                  <p className="font-medium">{identificationDocument.file.name}</p>
                  <p className="text-left text-muted-foreground">{formatFileSize(identificationDocument.file.size)}</p>
                </div>
                <div className="flex-grow" />
                <button
                  onClick={() => {
                    setUserDocuments((prev) =>
                      prev.filter((document) => document.documentType !== 'identificationDocument'),
                    );
                  }}
                >
                  <Trash className="h-8 w-8 rounded-lg border border-slate-200 p-1.5" />
                </button>
              </div>
            ) : (
              <Dropzone
                onDrop={(acceptedFiles) => {
                  uploadFile('identificationDocument', acceptedFiles[0]);
                }}
                accept={{
                  'application/pdf': ['.pdf'],
                  'image/jpeg': ['.jpg', '.jpeg'],
                  'image/png': ['.png'],
                }}
                maxFiles={1}
              >
                {({ getRootProps, getInputProps }) => (
                  <div {...getRootProps()}>
                    <input {...getInputProps()} />
                    <div className="flex h-16 flex-row items-center gap-4 rounded-lg border border-dashed border-slate-300 px-5 py-3">
                      <Upload className="h-6 w-6" />
                      <div>
                        <p className="text-sm font-medium">Upload document</p>
                        <p className="text-xs text-muted-foreground">
                          Drag and drop files here or {''}
                          <span className="text-blue-500">Choose File</span> (Max file size 2MB)
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </Dropzone>
            )}
            <p className="text-xs font-normal text-slate-500">Accepted documents include: PDF, JPG, PNG</p>
          </div>
        </div>
      )}

      {depositRequired && (
        <div>
          <hr className="my-4" />
          <div className="flex flex-col">
            <p className="text-sm font-semibold text-slate-900">Payment Deposit</p>
            <p className="text-xs font-normal text-slate-500">
              This auction requires a payment deposit upon registration. Please note that if you win, the deposit is
              deducted from the final amount; if you lose, the deposit is refunded.
            </p>
          </div>
          <div className="mt-4">
            <p className="text-xs font-normal text-slate-500">Deposit Amount</p>
            <p className="text-lg">{formatPrice(depositPercentage > 0 ? totalDeposit : (data?.depositAmount ?? 0))}</p>
          </div>
          <div className="mt-4 flex flex-col gap-2">
            {paymentDocument ? (
              <div className="flex h-16 flex-row items-center gap-4 rounded-lg border border-slate-200 px-5 py-3">
                <CloudUpload className="h-6 w-6" />
                <div>
                  <p className="font-medium">{paymentDocument.file.name}</p>
                  <p className="text-left text-muted-foreground">{formatFileSize(paymentDocument.file.size)}</p>
                </div>
                <div className="flex-grow" />
                <button
                  onClick={() => {
                    setPaymentDocument(null);
                  }}
                >
                  <Trash className="h-8 w-8 rounded-lg border border-slate-200 p-1.5" />
                </button>
              </div>
            ) : (
              <Dropzone
                onDrop={(acceptedFiles) => {
                  uploadFile('paymentDocument', acceptedFiles[0]);
                }}
                accept={{
                  'application/pdf': ['.pdf'],
                  'image/jpeg': ['.jpg', '.jpeg'],
                  'image/png': ['.png'],
                }}
                maxFiles={1}
              >
                {({ getRootProps, getInputProps }) => (
                  <div {...getRootProps()}>
                    <input {...getInputProps()} />
                    <div className="flex h-16 flex-row items-center gap-4 rounded-lg border border-dashed border-slate-300 px-5 py-3">
                      <Upload className="h-6 w-6" />
                      <div>
                        <p className="text-sm font-medium">Upload document</p>
                        <p className="text-xs text-muted-foreground">
                          Drag and drop files here or {''}
                          <span className="text-blue-500">Choose File</span> (Max file size 2MB)
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </Dropzone>
            )}
            <p className="text-xs font-normal text-slate-500">Accepted documents include: PDF, JPG, PNG</p>
          </div>
          <div className="mt-4 w-full">
            <div className="flex w-full flex-row items-center">
              <p className="w-1/3 text-sm font-normal sm:w-1/4">Account Name:</p>
              <p>{data?.auctionHouse?.accountName || '-'}</p>
            </div>
            <div className="flex w-full flex-row items-center">
              <p className="w-1/3 text-sm font-normal sm:w-1/4">Bank Name:</p>
              <p>{data?.auctionHouse?.bankName || '-'}</p>
            </div>
            <div className="flex w-full flex-row items-center">
              <p className="w-1/3 text-sm font-normal sm:w-1/4">Account Number:</p>
              <p>{data?.auctionHouse?.accountNumber || '-'}</p>
            </div>
          </div>
        </div>
      )}

      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Button
              className="mt-4 w-full"
              variant={'primary'}
              onClick={() => {
                const registrationFormData = new FormData();
                registrationFormData.append(
                  'status',
                  data?.approvalRequired ? AuctionRegistrationStatus.PENDING : AuctionRegistrationStatus.APPROVED,
                );
                registrationFormData.append('auctionId', data?.id || '');
                registrationFormData.append('userId', userId || '');

                if (userDocuments.length > 0) {
                  registrationFormData.append(
                    'documents',
                    JSON.stringify(
                      userDocuments.map((doc) => ({
                        src: doc.src,
                        name: doc.documentType,
                        type: doc.file.type,
                        size: doc.file.size,
                      })),
                    ),
                  );
                }
                if (paymentDocument) {
                  registrationFormData.append(
                    'paymentDocument',
                    JSON.stringify({
                      src: paymentDocument.src,
                      name: paymentDocument.documentType,
                      type: paymentDocument.file.type,
                      size: paymentDocument.file.size,
                    }),
                  );
                }

                const selectedProducts: Array<string> = [];
                Object.entries(checkedState).forEach(([productId, value]) => {
                  if (value) selectedProducts.push(productId);
                });

                if (selectedProducts.length > 0) {
                  registrationFormData.append('metadata', JSON.stringify({ selectedProducts }));
                }

                registrationFormData.append('action', 'createUpdateAuctionRegistration');

                fetcher.submit(registrationFormData, {
                  method: 'post',
                  action: `/product-detail/${slug}`,
                });
                setStep(6);
              }}
            >
              Submit
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Please upload your documents before submitting</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );

  const propertyDeposit = (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-center justify-center">
        <p className="text-xl font-medium text-black">Select Properties</p>
        <p className="text-pretty text-center text-sm font-normal text-slate-500">
          Review and select the properties that you want to bid.
        </p>
      </div>

      <div className="h-[1px] bg-slate-200" />
      <div>
        <p className="font-semibold text-slate-900">Payment Method</p>
        <p className="text-xs font-normal text-slate-600">
          This auction requires a payment deposit upon registration. Please note that if you win, the deposit is
          deducted from the final amount; if you lose, the deposit is refunded.
        </p>
        <p className="mt-2 text-sm text-red-500">
          *Please note that you may only bid on the property for which you have paid the deposit.
        </p>
      </div>
      <div className="-mb-4 h-[1px] bg-slate-200" />

      <div>
        {data?.products.map((product) => (
          <div className="flex flex-row items-center justify-between border-b border-slate-200 py-4" key={product.id}>
            <div className="flex w-3/5 flex-row items-center gap-2">
              <img
                src={product.images[0]?.file.src || '/images/placeholder-image-600x400.png'}
                alt="property-image"
                className="h-16 w-16"
              />
              <div className="flex flex-col">
                <p className="text-lg font-semibold">{product.name}</p>
                <p className="text-sm capitalize text-slate-500">
                  {'Lot: ' + product.lotNumber} ∙ {product.property?.tenure}
                </p>
                <p className="text-sm capitalize text-slate-500">
                  {'Reserved Price: ' + formatPrice(product.property?.reservedPrice ?? 0)}
                </p>
              </div>
            </div>
            <div>
              <p className="text-sm text-slate-500">
                Deposit Amount <span>({depositPercentage}%)</span>
              </p>
              <p className="font-medium">
                {formatPrice(((product.property?.reservedPrice ?? 0) * depositPercentage) / 100)}
              </p>
            </div>
            <Checkbox
              id={`select-property-${product.id}`}
              onCheckedChange={() => handleCheckedChange(product.id)}
              checked={checkedState[product.id]}
              className="ml-2 h-5 w-5"
            />
          </div>
        ))}
        <div className="flex flex-row items-center justify-between border-b border-slate-200 py-4 font-medium">
          <p>Total Amount Due</p>
          <p>{formatPrice(totalDeposit)}</p>
        </div>
      </div>
      <Button
        className="mt-4 w-full"
        variant={'primary'}
        disabled={Object.values(checkedState).every((value) => value === false)}
        onClick={() => setStep(5)}
      >
        Make Payment
      </Button>
    </div>
  );

  const existingUserLogin = (
    <ValidatedForm validator={loginValidation} method="post" action="/api/login" fetcher={fetcher}>
      <Form {...loginForm}>
        <input type="hidden" name="isSeller" value={String(false)} />
        <div className="flex flex-col gap-4">
          <div className="flex flex-col items-center justify-center">
            <p className="text-xl font-medium text-black">Login to Best2bid</p>
            <p className="text-sm font-normal text-slate-500">Please enter your login details to continue</p>
          </div>
          <div className="my-4 flex flex-col gap-2">
            <div className="w-full items-center gap-1.5">
              <FormField
                control={loginForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="mb-2">
                    <FormLabel>Email</FormLabel>
                    <Input type="email" id="email" placeholder="Email" {...field} />
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
            </div>
            <div className="w-full items-center gap-1.5">
              <FormField
                control={loginForm.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="mb-2">
                    <FormLabel>Password</FormLabel>
                    <PasswordInput id="password" placeholder="Password" {...field} />
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <Button type="submit" variant={'primary'} disabled={fetcher.state === 'submitting'}>
            {fetcher.state === 'submitting' && <LoaderCircle className="mr-2 animate-spin" />}
            Login
          </Button>
          <div className="flex flex-row items-center justify-center gap-2 text-sm font-semibold">
            <p className="text-slate-700">New here?</p>
            <Button
              variant={'ghost'}
              className="px-0 text-blue-500 hover:bg-transparent hover:text-blue-400 hover:underline"
              onClick={() => setStep(1)}
            >
              Create Account
            </Button>
          </div>
          <p className="text-xs font-normal text-slate-500">
            By continuing you agree to the <span className="text-blue-600">terms of service</span> and{' '}
            <span className="text-blue-600">privacy policy</span>. We may send you communications; you can set your
            preferences in your account.
          </p>
        </div>
      </Form>
    </ValidatedForm>
  );

  const awaitingApproval = (
    <div className="flex flex-col items-center justify-center gap-10">
      <p className="text-xl font-medium text-black">Awaiting Registration Approval</p>
      <p className="text-balance text-center text-sm font-normal text-slate-500">
        The auctioneer is now reviewing your registration. You will be notified as soon if your registration has been
        approved.
      </p>
      <Button
        className="w-full"
        variant={'primary'}
        onClick={() => {
          const registrationFormData = new FormData();
          registrationFormData.append(
            'status',
            data?.approvalRequired ? AuctionRegistrationStatus.PENDING : AuctionRegistrationStatus.APPROVED,
          );
          registrationFormData.append('auctionId', data?.id || '');
          registrationFormData.append('userId', userId || '');
          registrationFormData.append('action', 'createUpdateAuctionRegistration');

          const selectedProducts: Array<string> = [];
          Object.entries(checkedState).forEach(([productId, value]) => {
            if (value) selectedProducts.push(productId);
          });

          if (selectedProducts.length > 0) {
            registrationFormData.append('metadata', JSON.stringify({ selectedProducts }));
          }

          fetcher.submit(registrationFormData, {
            method: 'post',
            action: `/product-detail/${slug}`,
          });
          setOpen(false);
        }}
      >
        Close
      </Button>
      <div className="flex flex-row items-center justify-center gap-2">
        <Checkbox id="notify" className="border-slate-400" />
        <label
          htmlFor="terms"
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          Notify me when I&apos;m approved
        </label>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild className="mt-4">
        <Button variant={'primary'}>Register to Bid</Button>
      </DialogTrigger>
      <DialogContent className="max-h-screen overflow-auto sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>{step === 1 && 'Register to Bid'}</DialogTitle>
        </DialogHeader>
        {step === 1 && !isLoggedIn && createAccount}
        {step === 2 && createPaymentMethod}
        {step === 3 && createProfile}
        {step === 4 && termAndConditions}
        {step === 5 && uploadDocuments}
        {step === 6 &&
          (approvalRequired ? (
            awaitingApproval
          ) : (
            <div className="text-center">
              <p className="text-lg font-semibold">Registration Completed</p>
              <p className="mb-6 mt-2 text-slate-500">You&apos;re approved! Happy bidding!</p>
              <Button
                onClick={() => {
                  const registrationFormData = new FormData();
                  registrationFormData.append('status', AuctionRegistrationStatus.APPROVED);
                  registrationFormData.append('auctionId', data?.id || '');
                  registrationFormData.append('userId', userId || '');
                  registrationFormData.append('action', 'createUpdateAuctionRegistration');

                  fetcher.submit(registrationFormData, {
                    method: 'post',
                    action: `/product-detail/${slug}`,
                  });
                  setOpen(false);
                }}
                className="w-full"
                variant={'primary'}
              >
                Close
              </Button>
            </div>
          ))}
        {step === 7 && propertyDeposit}
        {step === 8 && existingUserLogin}
      </DialogContent>
    </Dialog>
  );
}
